import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Typography from "@mui/material/Typography";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentIcon from "@mui/icons-material/Payment";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EmailIcon from "@mui/icons-material/Email";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "components/CustomButtons/Button.js";

// Context & API Services
import { useAuth } from "../../context/AuthContext";
import UserApi from "../../api_calls/AuthApi";
import AccountApi from "../../api_calls/AccountApi";

// Toast Utility
import { showSuccess, showError } from "../../utils/toastUtils";

const MarketerSettings = () => {
  const { userId } = useParams();
  const { updateUserData } = useAuth();

  const [paypalEmail, setPaypalEmail] = useState("");
  const [mpesaNumber, setMpesaNumber] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  // ✅ Fetch data using the lightweight GET endpoint
  useEffect(() => {
    const loadData = async () => {
      setPageLoading(true);
      try {
        const result = await AccountApi.fetchFewUserDetails();

        if (result && result.status === "success") {
          const data = result.data;
          // Handle Python's str() conversion of None values from the backend
          setPaypalEmail(
            data.paypal_email === "None" ? "" : data.paypal_email || "",
          );
          setMpesaNumber(
            data.mpesa_number === "None" ? "" : data.mpesa_number || "",
          );
        }
      } catch (error) {
        console.error("Error loading payment settings:", error);
        showError("Could not load your settings.");
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveLoading(true);

    try {
      const result = await UserApi.updatePaymentDetails(
        paypalEmail,
        mpesaNumber,
      );

      if (result.status === "success") {
        // ✅ Using your react-hot-toast utility
        showSuccess("Payment details updated successfully!");

        // Sync global auth context so the rest of the app knows about the change
        if (updateUserData) {
          updateUserData({
            paypal_email: paypalEmail,
            mpesa_number: mpesaNumber,
          });
        }
      } else {
        showError(result.data || "Failed to update details.");
      }
    } catch (err) {
      showError("A system error occurred while saving.");
    } finally {
      setSaveLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "50px" }}
      >
        <CircularProgress color="rose" />
      </div>
    );
  }

  return (
    <div style={{ padding: "5px", maxWidth: "768px" }}>
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: "20px",
          fontFamily: "Calibri, sans-serif",
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <SettingsIcon sx={{ marginRight: "8px" }} /> Marketer Settings
      </Typography>

      <Typography
        sx={{ marginBottom: "24px", fontSize: "16px", color: "#666" }}
      >
        Configure your payout methods to receive commissions.
      </Typography>

      <form onSubmit={handleSave}>
        {/* PayPal Section */}
        <div
          style={{
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "12px",
            backgroundColor: "#fff",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <PaymentIcon sx={{ marginRight: "8px", color: "#d76213" }} /> PayPal
            Details
          </Typography>
          <TextField
            fullWidth
            label="PayPal Email Address"
            variant="outlined"
            value={paypalEmail}
            onChange={(e) => setPaypalEmail(e.target.value)}
            placeholder="example@email.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {/* M-Pesa Section */}
        <div
          style={{
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "12px",
            backgroundColor: "#fff",
            marginBottom: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <PhoneIphoneIcon sx={{ marginRight: "8px", color: "#d76213" }} />{" "}
            M-Pesa Details
          </Typography>
          <TextField
            fullWidth
            label="M-Pesa Phone Number"
            variant="outlined"
            value={mpesaNumber}
            onChange={(e) => setMpesaNumber(e.target.value)}
            placeholder="e.g. 0712345678"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountBalanceWalletIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div style={{ textAlign: "right" }}>
          <Button
            color="rose"
            type="submit"
            disabled={saveLoading}
            style={{ minWidth: "150px" }}
          >
            {saveLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MarketerSettings;
